import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { fmt, api } from "@/api";
import { Minus, Plus, X, Tag } from "lucide-react";
import { toast } from "sonner";

export default function Cart() {
  const { cart, updateQty, removeFromCart, subtotal } = useApp();
  const [code, setCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [couponCode, setCouponCode] = useState("");
  const nav = useNavigate();

  const shipping = subtotal >= 399 || subtotal === 0 ? 0 : 80;
  const total = Math.max(0, subtotal - discount) + shipping;
  const toGo = Math.max(0, 399 - subtotal);

  const applyCoupon = async () => {
    if (!code) return;
    try {
      const { data } = await api.post("/coupons/apply", { code, subtotal });
      setDiscount(data.discount); setCouponCode(data.code);
      toast.success(`Coupon applied! You saved ${fmt(data.discount)}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid coupon"); }
  };

  if (cart.length === 0) return (
    <div className="max-w-5xl mx-auto px-4 py-20 text-center">
      <div className="text-6xl mb-4">🛒</div>
      <h1 className="font-serif text-3xl">Your cart is empty</h1>
      <p className="text-muted-foreground mt-2">Explore our puja samagri, kits and idols.</p>
      <Link to="/" className="pk-btn-primary mt-6 inline-flex">Start Shopping</Link>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <h1 className="font-serif text-4xl">Your Cart</h1>
      <div className="mt-8 grid md:grid-cols-[1fr_360px] gap-8">
        <div className="space-y-3">
          {cart.map(c => (
            <div key={c.product_id} className="flex items-center gap-4 p-4 rounded-xl border border-border bg-white" data-testid={`cart-item-${c.product_id}`}>
              <img src={c.image} alt="" className="w-20 h-20 rounded-lg object-cover"/>
              <div className="flex-1">
                <div className="font-medium">{c.name}</div>
                {c.kit && <span className="inline-block mt-1 text-[10px] uppercase tracking-widest bg-secondary/30 px-2 py-0.5 rounded-full">Kit item</span>}
                <div className="text-sm text-primary font-semibold mt-1">{fmt(c.price)}</div>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid={`cart-dec-${c.product_id}`} onClick={()=>updateQty(c.product_id, c.qty-1)} className="w-8 h-8 rounded-full border border-border grid place-items-center"><Minus className="w-3 h-3"/></button>
                <span className="w-6 text-center font-medium">{c.qty}</span>
                <button data-testid={`cart-inc-${c.product_id}`} onClick={()=>updateQty(c.product_id, c.qty+1)} className="w-8 h-8 rounded-full border border-border grid place-items-center"><Plus className="w-3 h-3"/></button>
              </div>
              <div className="font-semibold w-24 text-right">{fmt(c.price*c.qty)}</div>
              <button data-testid={`cart-remove-${c.product_id}`} onClick={()=>removeFromCart(c.product_id)} className="p-2 text-muted-foreground hover:text-primary"><X className="w-4 h-4"/></button>
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-border bg-white p-6 h-fit sticky top-24">
          <h2 className="font-serif text-xl mb-4">Order Summary</h2>
          <div className="flex gap-2">
            <input data-testid="coupon-input" value={code} onChange={e=>setCode(e.target.value.toUpperCase())} placeholder="Coupon code" className="pk-input flex-1"/>
            <button data-testid="apply-coupon-btn" onClick={applyCoupon} className="px-4 rounded-lg bg-primary text-white text-sm">Apply</button>
          </div>
          <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1"><Tag className="w-3 h-3"/>Try WELCOME10 · PUJA200 · FESTIVE15</div>

          <div className="mt-5 space-y-2 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>{fmt(subtotal)}</span></div>
            {discount>0 && <div className="flex justify-between text-emerald-700"><span>Discount ({couponCode})</span><span>- {fmt(discount)}</span></div>}
            <div className="flex justify-between"><span>Shipping</span><span>{shipping===0?"FREE":fmt(shipping)}</span></div>
            {toGo > 0 ? (
              <div className="mt-2 rounded-lg bg-secondary/20 border border-secondary/30 p-2 text-xs text-primary">
                Add <b>{fmt(toGo)}</b> more to unlock <b>FREE SHIPPING 🚚</b>
              </div>
            ) : subtotal > 0 && (
              <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-200 p-2 text-xs text-emerald-800">
                🎉 Congratulations! You've unlocked <b>FREE SHIPPING</b>
              </div>
            )}
          </div>
          <div className="border-t border-border mt-4 pt-4 flex justify-between items-baseline">
            <span className="font-medium">Total</span>
            <span className="text-2xl font-semibold text-primary">{fmt(total)}</span>
          </div>
          <button data-testid="checkout-btn" onClick={()=>nav("/checkout", { state: { discount, couponCode } })} className="pk-btn-primary w-full mt-5">Proceed to Checkout</button>
          <Link to="/" className="mt-3 block text-center text-sm text-primary underline">Continue shopping</Link>
        </div>
      </div>
    </div>
  );
}
